import React, { useMemo, useCallback } from 'react';
import { Button, Input, Modal, Space, TableProps, message, Switch } from 'antd';
import type { SorterResult, FilterValue } from 'antd/es/table/interface';
import CommonTable from 'components/CommonTable';
import type { ColumnsType } from 'antd/es/table';
import s from './s.module.less';
import { SearchOutlined, PlusOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { PATH } from 'constants/path';
import dayjs from 'dayjs';
import type { TAssessmentItem, IAssessmentInput } from 'types/operation';
import AssessmentUpdateModal from '../AssessmentUpdateModal';
import ExternalLinkIcon from 'assets/common/externalLinkIcon.svg';
import {
    getAssessmentList,
    changeAssessmentStatus,
    addOrUpdateAssessment,
} from 'api/operation/assessment';
import { EnumFields } from 'types/enumerationData';
import useGetSettingData from 'hooks/useGetSettingData';
import { Meta } from 'types/common';
import { EPageStatus, EKlarityPageStatus } from 'types/helloKlarity';
import { isDevEnv, isTestEnv, isProductionEnv, isPrereleaseEnv } from 'utils/common';
import { EFeatureType } from 'constants/textVisit';

const Index = () => {
    const navigation = useNavigate();

    // Workflows state
    const [itemList, setItemList] = React.useState<TAssessmentItem[]>([]);
    const [total, setTotal] = React.useState<number>(0);
    const [loading, setLoading] = React.useState(false);
    const [, contextHolder] = Modal.useModal();
    const [openAssessmentModal, setOpenAssessmentModal] = React.useState(false);
    const [isOpenErrorModal, setIsOpenErrorModal] = React.useState(false);
    const [errorContent, setErrorContent] = React.useState<string[]>([]);
    const [itemStatus, setItemStatus] = React.useState<EPageStatus>(EPageStatus.ADD);
    const [assessmentItem, setAssessmentItem] =
        React.useState<TAssessmentItem>();
    const [searchOrderType, setOrderType] = React.useState<string>('DESC');
    const [searchOrderKey, setOrderKey] = React.useState<string>('UPDATE_AT');
    const pageSize = 10;

    const [page, setPage] = React.useState(1);
    const [pendingProviderNameFilter, setPendingProviderNameFilter] =
        React.useState('');
    const [pendingPartnerNameFilter, setPendingPartnerNameFilter] =
        React.useState('');
    const [pendingProviderIdFilter, setPendingProviderIdFilter] =
        React.useState('');
    const [nameFilter, setNameFilter] = React.useState<string>('');
    const [partnerNameFilter, setPartnerNameFilter] = React.useState<string>('');
    const [idFilter, setIdFilter] = React.useState<string>('');
    const [tagsFilter, setTagsFilter] = React.useState<string>('');
    const [statusFilter, setStatus] = React.useState<string>(
        '',
    );
    const [condition] =
        useGetSettingData(EnumFields.HELLO_KLARITY_CONDITION);
    const tagsOptions = useMemo(() => {
        if (!condition || !Array.isArray(condition)) {
            return [];
        }

        return (condition as Meta[])
            .filter((item: Meta) => item && item.label) // 过滤掉无效项
            .map((item: Meta) => {
                return {
                    text: item.label || '',
                    value: item.value || item.label || '',
                };
            })
            .sort((a, b) => {
                const stringA = (a.text || '').toLowerCase();
                const stringB = (b.text || '').toLowerCase();
                if (stringA < stringB) {
                    return -1;
                }
                if (stringA > stringB) {
                    return 1;
                }
                return 0;
            });
    }, [condition]);

    const doFetch = useCallback(async ({
        current,
        id,
        assessmentName,
        partnerName,
        orderKey,
        orderType,
        tags,
        status,
    }: IAssessmentInput) => {
        setLoading(true);
        const reqParam: IAssessmentInput = {
            current: current || page,
            size: pageSize,
            id: id || idFilter,
            assessmentName: assessmentName || nameFilter,
            partnerName: partnerName || partnerNameFilter,
            orderKey: orderKey || searchOrderKey,
            orderType: orderType || searchOrderType,
            tags: tags || tagsFilter || [],
            status: status || statusFilter || '',
            featureType: EFeatureType.TEXT_VISIT_WORKFLOW,
        };
        const result = await getAssessmentList(reqParam);
        if (!result.error) {
            setItemList(result?.data?.data?.records);
            setTotal(result?.data?.data?.total);
        }
        setLoading(false);
    }, [page, idFilter, nameFilter, partnerNameFilter, searchOrderKey, searchOrderType, tagsFilter, statusFilter]);

    const onTableChange: TableProps<TAssessmentItem>['onChange'] = (
        pagination,
        filters: Record<string, FilterValue | null>,
        sorter: SorterResult<TAssessmentItem> | SorterResult<TAssessmentItem>[],
    ) => {
        const pageInx = pagination?.current || 1;
        setPage(pageInx);
        const _tagsFilter = filters.conditionKeyTags as string[] || [];
        setTagsFilter(_tagsFilter.join(','));
        if (filters.status && Array.isArray(filters.status) && filters.status.length === 1) {
            setStatus(filters.status[0] === 'ACTIVE' ? 'A' : 'D');
        } else {
            setStatus('');
        }
        if (sorter && !Array.isArray(sorter) && sorter.order) {
            const orderType =
                sorter.order === 'ascend' ? 'ASC' : 'DESC';
            const orderKey =
                sorter.field === 'updateAt' ? 'UPDATE_AT' : (sorter.field as string || 'UPDATE_AT');
            setOrderType(orderType);
            setOrderKey(orderKey);
        }
    };

    const addNewAssessment = () => {
        setOpenAssessmentModal(true);
        setItemStatus(EPageStatus.ADD);
        setAssessmentItem(undefined);
    };

    const handleStatusChange = async (record: TAssessmentItem) => {
        const { data } = await changeAssessmentStatus({
            id: record.id || '',
            status:
                record.status === EKlarityPageStatus.ACTIVE
                    ? EKlarityPageStatus.INACTIVATE
                    : EKlarityPageStatus.ACTIVE,
        });
        if (Array.isArray(data?.data) && data?.data.length === 0) {
            message.success('Updated successfully');
            doFetch({});
        } else {
            setAssessmentItem(record);
            setIsOpenErrorModal(true);
            setErrorContent(data?.data || []);
        }
    };

    const handleEnableHelloKlarityDisplayChange = async (
        checked: boolean,
        record: TAssessmentItem,
    ) => {
        try {
            const result = await addOrUpdateAssessment({
                ...record,
                id: record.id,
                enableHelloKlarityDisplay: checked,
            });
            if (!result.error) {
                message.success('Updated successfully');
                doFetch({});
            } else {
                message.error('Update failed');
            }
        } catch (_error) {
            message.error('Update failed');
        }
    };

    const handleCancel = () => {
        setIsOpenErrorModal(false);
        setErrorContent([]);
    };

    const onSuccess = () => {
        setOpenAssessmentModal(false);
        setPage(1);
        doFetch({});
        message.success('Save successfully');
    };

    const handleDuplicate = (record: TAssessmentItem) => {
        setAssessmentItem(record);
        setItemStatus(EPageStatus.DUPLICATE);
        setOpenAssessmentModal(true);
    };

    const handleLink = (record: TAssessmentItem) => {
        if (isDevEnv()) {
            window.open(
                `https://dev.d2ysjydukc4efk.amplifyapp.com/awsome-embed?key=AK-test-api-key-1&uuid=${record.uuid}`,
            );
        }
        if (isTestEnv()) {
            window.open(
                `https://test.d2ysjydukc4efk.amplifyapp.com/awsome-embed?key=AK-test-api-key-1&uuid=${record.uuid}`,
            );
        }
        if (isProductionEnv() || isPrereleaseEnv()) {
            window.open(
                `https://www.helloklarity.com/awsome-embed?key=sk_prod_mh5dfc2mKXpI&uuid=${record.uuid}`,
            );
        }
    };

    React.useEffect(() => {
        doFetch({});
    }, [page, idFilter, nameFilter, partnerNameFilter, tagsFilter, searchOrderKey, searchOrderType, doFetch]);

    const columns: ColumnsType<TAssessmentItem> = [
        // {
        //     title: 'ID',
        //     dataIndex: 'id',
        //     key: 'id',
        //     width: '80px',
        //     filterDropdown: ({ close }: { close: () => void }) => (
        //         <div style={{ padding: 16 }} onKeyDown={(e) => e.stopPropagation()}>
        //             <Input
        //                 placeholder="Search id"
        //                 value={pendingProviderIdFilter}
        //                 onChange={(e) => {
        //                     setPendingProviderIdFilter(e.target.value);
        //                 }}
        //                 onPressEnter={() => {
        //                     setIdFilter(pendingProviderIdFilter);
        //                     setPage(1);
        //                     close();
        //                 }}
        //                 style={{ marginBottom: 8, display: 'block' }}
        //             />
        //             <Space>
        //                 <Button
        //                     type="primary"
        //                     onClick={() => {
        //                         setIdFilter(pendingProviderIdFilter);
        //                         setPage(1);
        //                         close();
        //                     }}
        //                     icon={<SearchOutlined />}
        //                     size="small"
        //                     style={{ width: 90 }}
        //                 >
        //                     Search
        //                 </Button>
        //                 <Button
        //                     onClick={() => {
        //                         setPendingProviderIdFilter('');
        //                         setIdFilter('');
        //                         setPage(1);
        //                         close();
        //                     }}
        //                     size="small"
        //                     style={{ width: 90 }}
        //                 >
        //                     Reset
        //                 </Button>
        //             </Space>
        //         </div>
        //     ),
        //     filterIcon: (filtered: boolean) => (
        //         <SearchOutlined style={{ color: filtered ? '#1677ff' : undefined }} />
        //     ),
        // },
        {
            title: 'ICON',
            dataIndex: 'iconUrl',
            key: 'iconUrl',
            width: '100px',
            render: (iconUrl: string) => {
                return iconUrl ? (
                    <img src={iconUrl} alt="icon" className={s.iconImage} />
                ) : (
                    <div className={s.iconPlaceholder}>-</div>
                );
            },
        },
        {
            title: 'WORKFLOW NAME',
            dataIndex: 'assessmentName',
            key: 'assessmentName',
            width: '220px',
            filterDropdown: ({ close }: { close: () => void }) => (
                <div style={{ padding: 16 }} onKeyDown={(e) => e.stopPropagation()}>
                    <Input
                        placeholder="Search name"
                        value={pendingProviderNameFilter}
                        onChange={(e) => {
                            setPendingProviderNameFilter(e.target.value);
                        }}
                        onPressEnter={() => {
                            setNameFilter(pendingProviderNameFilter);
                            setPage(1);
                            close();
                        }}
                        style={{ marginBottom: 8, display: 'block' }}
                    />
                    <Space>
                        <Button
                            type="primary"
                            onClick={() => {
                                setNameFilter(pendingProviderNameFilter);
                                setPage(1);
                                close();
                            }}
                            icon={<SearchOutlined />}
                            size="small"
                            style={{ width: 90 }}
                        >
                            Search
                        </Button>
                        <Button
                            onClick={() => {
                                setPendingProviderNameFilter('');
                                setNameFilter('');
                                setPage(1);
                                close();
                            }}
                            size="small"
                            style={{ width: 90 }}
                        >
                            Reset
                        </Button>
                    </Space>
                </div>
            ),
            filterIcon: (filtered: boolean) => (
                <SearchOutlined style={{ color: filtered ? '#1677ff' : undefined }} />
            ),
        },
        {
            title: 'CONDITION',
            dataIndex: 'conditionKeyTags',
            width: '300px',
            filterMultiple: false,
            filters: tagsOptions,
            render: (tags: string) => {
                return <div>{tags || '-'}</div>;
            },
        },
        {
            title: 'PARTNER NAME',
            dataIndex: 'partnerName',
            key: 'partnerName',
            width: '220px',
            render: (val: string) => <div>{val || '-'}</div>,
            filterDropdown: ({ close }: { close: () => void }) => (
                <div style={{ padding: 16 }} onKeyDown={(e) => e.stopPropagation()}>
                    <Input
                        placeholder="Search partner"
                        value={pendingPartnerNameFilter}
                        onChange={(e) => {
                            setPendingPartnerNameFilter(e.target.value);
                        }}
                        onPressEnter={() => {
                            setPartnerNameFilter(pendingPartnerNameFilter);
                            setPage(1);
                            close();
                        }}
                        style={{ marginBottom: 8, display: 'block' }}
                    />
                    <Space>
                        <Button
                            type="primary"
                            onClick={() => {
                                setPartnerNameFilter(pendingPartnerNameFilter);
                                setPage(1);
                                close();
                            }}
                            icon={<SearchOutlined />}
                            size="small"
                            style={{ width: 90 }}
                        >
                            Search
                        </Button>
                        <Button
                            onClick={() => {
                                setPendingPartnerNameFilter('');
                                setPartnerNameFilter('');
                                setPage(1);
                                close();
                            }}
                            size="small"
                            style={{ width: 90 }}
                        >
                            Reset
                        </Button>
                    </Space>
                </div>
            ),
            filterIcon: (filtered: boolean) => (
                <SearchOutlined style={{ color: filtered ? '#1677ff' : undefined }} />
            ),
        },
        {
            title: 'STATUS',
            dataIndex: 'status',
            width: '160px',
            filters: [
                {
                    text: 'Active',
                    value: 'ACTIVE',
                },
                {
                    text: 'Inactive',
                    value: 'INACTIVE',
                },
            ],
            render: (val: string) => {
                return (
                    <div className={s.cell}>
                        {val === 'A' && 'Active'}
                        {val === 'D' && 'Inactive'}
                    </div>
                );
            },
        },
        {
            title: 'LAST MODIFIED',
            dataIndex: 'updateAt',
            sortDirections: ['ascend', 'descend', 'ascend'],
            sorter: true,
            width: '150px',
            className: s.tableColumn,
            render: (time: string) => {
                return <div>{time ? dayjs(time).format('MM/DD/YYYY') : 'Nil'}</div>;
            },
        },
        {
            title: 'QUESTION FROM',
            dataIndex: 'questionsFormLink',
            width: '150px',
            className: s.tableColumn,
            render: (val: string) => {
                return val ? (
                    <a href={val} target="_blank" className={s.link} rel="noreferrer">
                        Form link
                        <img src={ExternalLinkIcon} />
                    </a>
                ) : (
                    '-'
                );
            },
        },
        {
            title: 'RESULTS',
            dataIndex: 'questionsFormResultLink',
            width: '150px',
            className: s.tableColumn,
            render: (val: string) => {
                return val ? (
                    <a href={val} target="_blank" className={s.link} rel="noreferrer">
                        Result link
                        <img src={ExternalLinkIcon} />
                    </a>
                ) : (
                    '-'
                );
            },
        },
        {
            title: 'DISPLAY',
            dataIndex: 'enableHelloKlarityDisplay',
            width: '120px',
            className: s.tableColumn,
            render: (val: boolean, record: TAssessmentItem) => {
                return (
                    <Switch
                        checked={val || false}
                        onChange={(checked) =>
                            handleEnableHelloKlarityDisplayChange(checked, record)
                        }
                    />
                );
            },
        },
        {
            title: '',
            width: '200px',
            fixed: 'right',
            render: (p: TAssessmentItem) => {
                return (
                    <div className={s.actionWrap}>
                        <Button
                            className={s.link}
                            onClick={() => handleStatusChange(p)}
                            type="text"
                        >
                            {p.status === EKlarityPageStatus.ACTIVE
                                ? 'De-activate'
                                : 'Activate'}
                        </Button>
                        <span
                            onClick={() => {
                                handleDuplicate(p);
                            }}
                            className={s.link}
                        >
                            Duplicate
                        </span>
                        {p.status === EKlarityPageStatus.ACTIVE ? (
                            <span
                                onClick={() => {
                                    handleLink(p);
                                }}
                                className={s.link}
                            >
                                Link
                                <img src={ExternalLinkIcon} />
                            </span>
                        ) : (
                            <span className={s.emptyLink}>-</span>
                        )}
                        <Button
                            className={s.link}
                            onClick={() => {
                                navigation(
                                    `${PATH.OPERATION}/${PATH.EMBEDDABLE_WORKFLOWS_DETAIL}?id=${p.id}&uuid=${p.uuid}`,
                                );
                            }}
                            type="text"
                        >
                            View
                        </Button>
                    </div>
                );
            },
        },
    ].filter(Boolean) as ColumnsType<TAssessmentItem>;

    return (
        <div className={s.wrap}>
            <div className={s.selectBar}>
                <Button onClick={addNewAssessment} icon={<PlusOutlined />}>
                    Add a new workflow
                </Button>
            </div>
            <CommonTable
                bordered
                rowKey="id"
                pagination={{
                    total,
                    current: page,
                    pageSize,
                    showSizeChanger: false,
                }}
                scroll={{ x: 'max-content' }}
                loading={loading}
                columns={columns}
                onChange={onTableChange}
                data={itemList || []}
            />
            {openAssessmentModal && (
                <AssessmentUpdateModal
                    item={assessmentItem}
                    itemStatus={itemStatus}
                    onCancel={() => setOpenAssessmentModal(false)}
                    onSuccess={onSuccess}
                />
            )}
            <Modal
                title=""
                open={isOpenErrorModal}
                destroyOnClose
                okText="Save"
                onCancel={handleCancel}
                closeIcon={<span />}
                footer={
                    <div>
                        <Button onClick={handleCancel}>Cancel</Button>
                        <Button
                            type="primary"
                            onClick={() => {
                                navigation(
                                    `${PATH.OPERATION}/${PATH.EMBEDDABLE_WORKFLOWS_DETAIL}?id=${assessmentItem?.id}&uuid=${assessmentItem?.uuid}`,
                                );
                            }}
                        >
                            Edit
                        </Button>
                    </div>
                }
            >
                <div className={s.popupContent}>
                    <div className={s.popupContentInner}>
                        <h3 className={s.popupTitle}>
                            This test cannot be activated due to missing
                            information
                        </h3>
                        <ul className={s.popupDetail}>
                            {errorContent.map((item: string, index: number) => {
                                return <li key={index}>{item}</li>;
                            })}
                        </ul>
                    </div>
                </div>
            </Modal>
            {contextHolder}
        </div>
    );
};

export default Index;
